import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiSend, FiLoader } = FiIcons;

const PublicForm = () => {
  const { id } = useParams();
  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchForm = async () => {
      const { data, error } = await supabase
        .from('lead_forms_1740000000000')
        .select('*')
        .eq('id', id)
        .single();
      
      if (data) setFormConfig(data);
      setLoading(false);
    };
    fetchForm();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const standardFields = ['name', 'email', 'phone', 'company', 'notes'];
    const contactPayload = {
      user_id: formConfig.user_id,
      custom_values: {}
    };

    Object.entries(formData).forEach(([key, value]) => {
      if (standardFields.includes(key)) {
        contactPayload[key] = value;
      } else if (key.startsWith('custom_')) {
        const fieldId = key.replace('custom_', '');
        contactPayload.custom_values[fieldId] = value;
      }
    });

    const { error } = await supabase.from('contacts_1740000000000').insert([contactPayload]);

    if (!error) {
      setSubmitted(true);
      if (formConfig.redirect_url) {
        setTimeout(() => window.location.href = formConfig.redirect_url, 2000);
      }
    }
    setSubmitting(false);
  };

  if (loading) return <div className="p-20 text-center"><SafeIcon icon={FiLoader} className="animate-spin text-4xl text-blue-600 mx-auto" /></div>;
  if (!formConfig) return <div className="p-20 text-center text-slate-400 font-bold">Form not found.</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-4">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon icon={FiCheckCircle} className="text-4xl" />
          </div>
          <h2 className="text-2xl font-black text-slate-900">Submitted!</h2>
          <p className="text-slate-500 font-medium">{formConfig.success_message}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden"
      >
        <div className="p-10 bg-slate-900 text-white">
          <h1 className="text-3xl font-black tracking-tighter mb-2">{formConfig.title || formConfig.name}</h1>
          <p className="text-slate-400 font-medium">{formConfig.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-10 space-y-6">
          {formConfig.fields.map(field => (
            <div key={field.id} className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              <input 
                required={field.required}
                type={field.type || 'text'}
                className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold focus:ring-2 focus:ring-blue-500 transition-all"
                value={formData[field.id] || ''}
                onChange={e => setFormData({...formData, [field.id]: e.target.value})}
              />
            </div>
          ))}

          <button 
            type="submit" 
            disabled={submitting}
            className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center justify-center gap-3"
          >
            {submitting ? 'Sending...' : (formConfig.button_text || 'Submit')}
            {!submitting && <SafeIcon icon={FiSend} />}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default PublicForm;